import { useStyletron } from '@tigergraph/app-ui-lib/Theme';
import { Label } from '@tigergraph/app-ui-lib/typography';
import { Clock } from 'lucide-react';
import { MdEdit, MdDeleteOutline } from 'react-icons/md';
import { styled } from '@tigergraph/app-ui-lib/Theme';
import { TimezonePicker } from '@tigergraph/app-ui-lib/timezonepicker';
import { Select } from '@tigergraph/app-ui-lib/select';
import { UseFormReturn } from 'react-hook-form';
import { type AdvancedType } from '@/pages/workgroup/form/WorkSpaceSubForm';
import { FormTitleContainer } from '@/components/styled';
import TooltipLabel from '@/components/TooltipLabel';
import { Checkbox } from '@tigergraph/app-ui-lib/checkbox';
import { STYLE_TYPE } from 'baseui/checkbox';
import { getSupportMailLink, getErrorMessage } from '@/utils/utils';
import { useOrgContext } from '@/contexts/orgContext';
import { type WorkspaceT } from '@/pages/workgroup/type';
import { useState } from 'react';
import UpgradeVersionModal from '@/pages/workgroup/form/setting/UpgradeVersionModal';
import { Button } from '@tigergraph/app-ui-lib/button';
import { useMutationUpgradeWorkspace, useQueryUpgradeVersions } from '@/pages/workgroup/hook';
import toast from 'react-hot-toast';
import mediaQuery from '@/lib/breakpoints';

export const Title = styled('div', ({ $theme }) => {
  return {
    marginBottom: '8px',
    fontWeight: 500,
    color: $theme.colors['dropdown.text'],
  };
});

export default function Maintenance({
  form,
  workspace,
  onClose,
}: {
  form: UseFormReturn<AdvancedType, any, undefined>;
  workspace?: WorkspaceT;
  onClose: () => void;
}) {
  const [css, theme] = useStyletron();

  const { setValue, watch } = form;

  const [showUpgradeVersionModal, setShowUpgradeVersionModal] = useState(false);

  const updateStrategy = watch('update_strategy');
  const desiredTgVersion = watch('desired_tg_version');
  const autoUpgradeState = watch('auto_upgrade_state');

  const startHour = watch('maintenance_window_hour_of_day');
  const day = watch('maintenance_window_day_of_week');
  const timezone = watch('maintenance_window_time_zone');

  const { currentOrg, userInfo } = useOrgContext();
  const url = getSupportMailLink({ userInfo, currentOrg, subject: 'Automatic Upgrade' });

  const hasManualUpgrade = !!desiredTgVersion && desiredTgVersion !== 'auto' && autoUpgradeState === 'scheduled';

  const showAutomaticUpgradeInfo = updateStrategy === 'update_strategy_auto_maintenance';

  const { mutateAsync: upgradeWorkspace } = useMutationUpgradeWorkspace();
  const { tgVersions: upgradeVersions } = useQueryUpgradeVersions();

  return (
    <div>
      {/* Maintenance Window - always configurable */}
      <FormTitleContainer
        className={css({
          marginBottom: '0px',
        })}
      >
        <TooltipLabel
          label="Maintenance Window"
          tooltip={
            'Configure the maintenance window for automatic maintenance updates or for manual feature/preview updates.'
          }
        />
      </FormTitleContainer>
      <Label
        className={css({
          color: theme.colors['text.secondary'],
          marginTop: '8px',
        })}
      >
        Configure the maintenance window for automatic maintenance updates or for manual feature/preview updates.
      </Label>
      <div
        className={css({
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          width: '100%',
          paddingTop: '8px',
        })}
      >
        <div
          className={css({
            width: '100%',
            padding: '16px',
            border: `1px solid ${theme.colors['border.tertiary']}`,
            borderRadius: '4px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            backgroundColor: theme.colors['background.secondary'],
          })}
        >
          <div>
            <Title>Day of the week</Title>
            <div
              className={css({
                display: 'flex',
                gap: '16px',
                [mediaQuery.xsmall]: {
                  gap: '8px',
                },
              })}
            >
              {days.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  className={css({
                    lineHeight: '16px',
                    height: '32px',
                    width: '32px',
                    borderRadius: '50%',
                    flexShrink: 0,
                    textAlign: 'center',
                    color: d.id === day ? theme.colors['datepicker.text.selected'] : theme.colors['datepicker.text'],
                    backgroundColor:
                      d.id === day
                        ? theme.colors['datepicker.background.selected']
                        : theme.colors['datepicker.background.hover'],
                  })}
                  onClick={() => setValue('maintenance_window_day_of_week', d.id)}
                >
                  {d.label.slice(0, 2)}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Title>Timezone</Title>
            <div
              className={css({
                width: '100%',
              })}
            >
              <TimezonePicker value={timezone} onChange={({ id }) => setValue('maintenance_window_time_zone', id)} />
            </div>
          </div>
          <div>
            <Title>Hour</Title>
            <div
              className={css({
                width: '100%',
              })}
            >
              <Select
                value={[{ id: startHour }]}
                onChange={(params) => {
                  setValue('maintenance_window_hour_of_day', params.value[0].id as number);
                }}
                options={hours}
                maxDropdownHeight="400px"
                clearable={false}
              />
            </div>
            <Label
              className={css({
                color: theme.colors['text.secondary'],
                marginTop: '8px',
              })}
            >
              {formatHour(startHour)} in your local time.
            </Label>
          </div>
        </div>

        {/* Automatic Maintenance Upgrades - independent toggle */}
        <FormTitleContainer
          className={css({
            marginBottom: '0px',
          })}
        >
          <TooltipLabel
            label="Automatic Maintenance Upgrades"
            tooltip={
              'To ensure you always have the latest features and security improvements, your workspace will automatically upgrade to the most recent stable version—only thoroughly tested releases are deployed.'
            }
          />
          <Checkbox
            checked={showAutomaticUpgradeInfo}
            labelPlacement="left"
            onChange={() => {
              if (showAutomaticUpgradeInfo) {
                setValue('update_strategy', 'update_strategy_never');
              } else {
                setValue('update_strategy', 'update_strategy_auto_maintenance');
              }
            }}
            checkmarkType={STYLE_TYPE.toggle_round}
          />
        </FormTitleContainer>

        {showAutomaticUpgradeInfo && (
          <div
            className={css({
              marginTop: '-8px',
              padding: '14px 12px',
              color: theme.colors['text.secondary'],
              borderRadius: '4px',
              border: `1px solid ${theme.colors['border.tertiary']}`,
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              backgroundColor: theme.colors['background.primary'],
              width: '100%',
            })}
          >
            <div
              className={css({
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              })}
            >
              <Clock size={20} color={theme.colors['icon.primary']} />
              <span>
                Maintenance Version Upgrade is scheduled every{' '}
                <span
                  className={css({
                    color: theme.colors['text.primary'],
                    fontWeight: 'bold',
                  })}
                >
                  {days.find((d) => d.id === day)?.label}
                </span>{' '}
                at{' '}
                <span
                  className={css({
                    color: theme.colors['text.primary'],
                    fontWeight: 'bold',
                  })}
                >
                  {formatHour(startHour)}
                </span>{' '}
                ({timezone}).
              </span>
            </div>
            <span
              className={css({
                fontSize: '12px',
                lineHeight: '16px',
              })}
            >
              The upgrade process may take over 2 hours to complete. If it exceeds this timeframe, please don&apos;t
              hesitate to{' '}
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                className={css({
                  color: theme.colors.secondary800,
                })}
              >
                contact us
              </a>{' '}
              for assistance. The workspace needs to be in an active state to perform automatic upgrade.
            </span>
          </div>
        )}

        {/* Manual Upgrade */}
        <div
          className={css({
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            width: '100%',
          })}
        >
          <div>
            <Button
              type="button"
              kind="secondary"
              disabled={hasManualUpgrade}
              onClick={() => {
                setShowUpgradeVersionModal(true);
              }}
            >
              Manual Upgrade
            </Button>
          </div>

          {hasManualUpgrade && (
            <div
              className={css({
                padding: '14px 12px',
                color: theme.colors['text.secondary'],
                borderRadius: '4px',
                border: `1px solid ${theme.colors['border.tertiary']}`,
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                backgroundColor: theme.colors['background.primary'],
              })}
            >
              <div
                className={css({
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                })}
              >
                <div
                  className={css({
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  })}
                >
                  <Clock size={20} color={theme.colors['icon.primary']} />
                  <span>
                    Upgrade to{' '}
                    <span
                      className={css({
                        color: theme.colors['text.primary'],
                        fontWeight: 'bold',
                      })}
                    >
                      TigerGraph {desiredTgVersion}
                    </span>{' '}
                    scheduled for next{' '}
                    <span
                      className={css({
                        color: theme.colors['text.primary'],
                        fontWeight: 'bold',
                      })}
                    >
                      {days.find((d) => d.id === day)?.label}
                    </span>{' '}
                    at{' '}
                    <span
                      className={css({
                        color: theme.colors['text.primary'],
                        fontWeight: 'bold',
                      })}
                    >
                      {formatHour(startHour)}
                    </span>{' '}
                    ({timezone}).
                  </span>
                </div>
                <div
                  className={css({
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  })}
                >
                  <Button
                    kind="text"
                    size="compact"
                    shape="square"
                    type="button"
                    onClick={() => {
                      setShowUpgradeVersionModal(true);
                    }}
                  >
                    <MdEdit size={16} color={theme.colors['button.icon']} />
                  </Button>
                  <Button
                    kind="text"
                    size="compact"
                    shape="square"
                    type="button"
                    onClick={() => {
                      setValue('desired_tg_version', 'auto');
                      setValue('auto_upgrade_state', 'none');
                    }}
                  >
                    <MdDeleteOutline size={16} color={theme.colors['button.icon.danger']} />
                  </Button>
                </div>
              </div>
            </div>
          )}
          {hasManualUpgrade && (
            <span
              className={css({
                fontSize: '12px',
                lineHeight: '16px',
                color: theme.colors['text.secondary'],
              })}
            >
              If this scheduled manual upgrade overlaps with a Maintenance upgrade, the Feature/Preview upgrade will
              take precedence.
            </span>
          )}
        </div>
      </div>

      {showUpgradeVersionModal && (
        <UpgradeVersionModal
          isOpen={showUpgradeVersionModal}
          onClose={() => {
            setShowUpgradeVersionModal(false);
          }}
          onConfirm={({ version, timing }) => {
            if (timing === 'now' && workspace) {
              const promise = upgradeWorkspace({
                workgroup_id: workspace.workgroup_id,
                workspace_id: workspace.workspace_id,
                version,
                scheduled: false,
              });
              toast.promise(promise, {
                loading: 'Starting upgrade...',
                success: 'Workspace upgrade started successfully!',
                error: (err) => `Failed to start upgrade: ${getErrorMessage(err as Error)}`,
              });
              setShowUpgradeVersionModal(false);

              // after upgrade, we just close the modal
              setValue('desired_tg_version', 'auto');
              setValue('auto_upgrade_state', 'none');

              // just close the drawer after manual upgrade to simplify the flow
              onClose();

              return;
            }
            // schedule during maintenance window
            setValue('auto_upgrade_state', 'scheduled');
            setValue('desired_tg_version', version);
            setShowUpgradeVersionModal(false);
          }}
          versions={upgradeVersions}
          desiredTgVersion={desiredTgVersion}
          workspace={workspace}
          defaultTiming={hasManualUpgrade ? 'schedule' : 'now'}
        />
      )}
    </div>
  );
}

const days = [
  {
    id: 0,
    label: 'Sunday',
  },
  {
    id: 1,
    label: 'Monday',
  },
  {
    id: 2,
    label: 'Tuesday',
  },
  {
    id: 3,
    label: 'Wednesday',
  },
  {
    id: 4,
    label: 'Thursday',
  },
  {
    id: 5,
    label: 'Friday',
  },
  {
    id: 6,
    label: 'Saturday',
  },
];

const hours = Array.from({ length: 24 }, (_, i) => i).map((i) => ({
  id: i,
  label: formatHour(i),
}));

function formatHour(hour: number) {
  const endHour = (hour + 2) % 24;
  return `${hour.toString().padStart(2, '0')}:00 - ${endHour.toString().padStart(2, '0')}:00`;
}
